import {Button, Popconfirm, Table, Tooltip} from "antd";
import React, {useRef} from "react";
import sorters from "../../util/sorters";
import moment from "moment/moment";
import {PREFERRED_DATE_FORMAT, PREFERRED_DATETIME_FORMAT} from "../../util/time";
import {changeD365PaymentInvoice, updateD365PaymentStatus} from "../../api/d365Api";
import {handleResponse} from "./utils";
import {PaymentChangeButton} from "./PaymentChangeButton";
import {useDynamicTableHeight} from "../../util/useDynamicTableHeight";
import {getPaginationConfig} from "../../util/showAllPagination";


const PaymentsTable = ({
                         records,
                         load,
                         loading,
                         setLoading,
                         permissions,
                         hideColumns
                       }) => {
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef,210);

  const getStatusChangeTitle = (status) => {
    switch (status) {
      case 'IGNORED':
        return 'Change status to NOT FOUND, so it will be retried next cycle';
      case 'NOT FOUND':
        return 'Change status to IGNORED';
      case 'PAID':
        return 'Mark invoice as unpaid and change status to IGNORED';
      case 'OPEN':
        return 'Change status to IGNORED';
      default:
        return 'Incorrect status';
    }
  }

  const updatePaymentStatus = (id, status) => {
    setLoading(true);
    updateD365PaymentStatus(id, status).then(response => {
      handlePaymentsResponse(response, 'Status', 'update');
    }).finally(() => {
      setLoading(false)
    });
  }

  const handlePaymentsResponse = (response, description, action) => {
    handleResponse(response, description, action);
    load();
  };

  const _onPaymentChange = (id, invoice) => {
    setLoading(true);
    changeD365PaymentInvoice(id, invoice).then(response => {
      handlePaymentsResponse(response, 'Invoice', 'change');

    }).finally(() => {
      setLoading(false);
    })
  };


  const columns = [
    {
      title: 'ID',
      width: 80,
      align: 'right',
      sorter: (a, b) => sorters.number(a.ID, b.ID),
      dataIndex: 'ID',
      hidden: hideColumns
    },
    {
      title: 'Created',
      width: 165,
      sorter: (a, b) => sorters.string(a.CreatedAt, b.CreatedAt),
      render: r => moment(r.CreatedAt).format(PREFERRED_DATETIME_FORMAT),
      hidden: hideColumns
    }, {
      title: 'Updated',
      width: 165,
      sorter: (a, b) => sorters.string(a.UpdatedAt, b.UpdatedAt),
      render: r => moment(r.UpdatedAt).format(PREFERRED_DATETIME_FORMAT),
      hidden: hideColumns
    }, {
      title: 'Updated By',
      sorter: (a, b) => sorters.string(a.updatedBy, b.updatedBy),
      dataIndex: 'updatedBy',
      hidden: hideColumns
    }, {
      title: 'Pending Invoice Number',
      sorter: (a, b) => sorters.string(a.pendingInvoiceNumber, b.pendingInvoiceNumber),
      dataIndex: 'pendingInvoiceNumber'
    }, {
      title: 'Voucher',
      sorter: (a, b) => sorters.string(a.voucher, b.voucher),
      dataIndex: 'voucher'
    }, {
      title: 'Invoice Number',
      sorter: (a, b) => sorters.string(a.invoiceNumber, b.invoiceNumber),
      dataIndex: 'invoiceNumber'
    }, {
      title: 'Date',
      sorter: (a, b) => sorters.string(a.date, b.date),
      render: r => moment(r.date).format(PREFERRED_DATE_FORMAT)
    }, {
      title: 'Projector Invoice',
      sorter: (a, b) => sorters.string(a.projectorInvoice, b.projectorInvoice),
      dataIndex: 'projectorInvoice'
    }, {
      title: 'Projector Name',
      sorter: (a, b) => sorters.string(a.projectorName, b.projectorName),
      dataIndex: 'projectorName'
    }, {
      title: 'Projector Status',
      sorter: (a, b) => sorters.string(a.projectorStatus, b.projectorStatus),
      dataIndex: 'projectorStatus'
    }, {
      title: 'Projector Amount',
      sorter: (a, b) => sorters.number(a.projectorAmount, b.projectorAmount),
      align: 'right',
      render: r => r.projectorAmount === 0.0 ? '' : r.projectorAmount.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        style: 'currency',
        currency: 'USD'
      })
    }, {
      title: 'Projector Date',
      sorter: (a, b) => sorters.date(a.projectorDate, b.projectorDate),
      render: r => r.projectorDate === '0001-01-01T00:00:00Z' ? '' : moment(r.projectorDate).format(PREFERRED_DATE_FORMAT)
    }, {
      title: 'Status',
      sorter: (a, b) => sorters.string(a.status, b.status),
      render: r => (
        permissions !== null && permissions.canManageD365 ?
          (
            <Popconfirm title={getStatusChangeTitle(r.status)}
                        onConfirm={() => updatePaymentStatus(r.ID, r.status)}
                        okText='Yes' cancelText='No'

            >
              <Tooltip title={'Click to change status'} align='bottom' placement='bottom'>
                <Button type='link'>{r.status}</Button>
              </Tooltip></Popconfirm>
          ) :
          r.status
      )
    }, {
      title: 'Action',
      width: 100,
      align: 'center',
      render: r => (
        permissions !== null && permissions.canManageD365 ?
          (
            <PaymentChangeButton record={r} onPaymentChange={_onPaymentChange}/>
          ) :
          ''
      )
    }
  ].filter(item => !item.hidden);

  return (
    <>
      <Table
        ref={tableContainerRef}
        rowKey='ID'
        dataSource={records}
        columns={columns}
        pagination={getPaginationConfig(records.length)}
        size='small'
        loading={loading}
        scroll={{y: tableHeight}}
      />
    </>
  );
}

export {PaymentsTable};